// src/components/VideoHero/VideoHero.jsx
import React, { useEffect, useRef, useState } from "react";

export default function VideoHero({
  mp4Src,
  webmSrc,
  poster,
  children,
  className = "h-[32vh] min-h-[260px]",
  overlayClass = "",
}) {
  const wrapRef = useRef(null);
  const videoRef = useRef(null);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [ready, setReady] = useState(false);
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updateReduced = () => setReduced(mql.matches);
    updateReduced();
    mql.addEventListener?.("change", updateReduced);

    const io = new IntersectionObserver(
      ([entry]) => entry.isIntersecting && setShouldLoad(true),
      { rootMargin: "200px 0px", threshold: 0.01 }
    );
    if (wrapRef.current) io.observe(wrapRef.current);

    return () => {
      io.disconnect();
      mql.removeEventListener?.("change", updateReduced);
    };
  }, []);

  // Koitetaan käynnistää toisto heti kun video mountataan
  useEffect(() => {
    if (!shouldLoad || reduced) return;
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    v.playsInline = true;
    const p = v.play();
    if (p && typeof p.catch === "function") {
      p.catch(() => {
        // jos selain blokkaa automaattisoiton, annetaan posterin olla näkyvissä
      });
    }
  }, [shouldLoad, reduced]);

  return (
    <header ref={wrapRef} className={`relative w-full overflow-hidden ${className}`}>
      {/* Poster: näkyy kunnes video on oikeasti valmis */}
      {poster && (
        <img
          src={poster}
          alt=""
          aria-hidden="true"
          className={`absolute inset-0 w-full h-full object-cover object-center transition-opacity duration-700 ${
            ready ? "opacity-0" : "opacity-100"
          }`}
          loading="eager"
          fetchPriority="high"
        />
      )}

      {/* Video mountataan vasta kun näkyvissä + ei reduced-motion */}
      {shouldLoad && !reduced && (
        <video
          ref={videoRef}
          className="absolute inset-0 w-full h-full object-cover object-center"
          muted
          loop
          autoPlay
          playsInline
          preload="metadata"
          poster={poster}
          onLoadedData={() => setReady(true)}     // varmempi kuin canplaythrough
          onError={(e) => {
            // Avaa Network-välilehdeltä lähde – jos 404 tai HTML, polku on väärä
            // console.error("Video error", e);
          }}
        >
          {webmSrc && <source src={webmSrc} type="video/webm" />}
          {mp4Src && <source src={mp4Src} type="video/mp4" />}
        </video>
      )}

      <div className={`absolute inset-0 ${overlayClass}`} />
      <div className="relative z-10 flex h-full items-center">
        <div className="mx-auto max-w-7xl w-full px-4 sm:px-6 lg:px-8">{children}</div>
      </div>
    </header>
  );
}
